import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Box, toast } from '@node-space/ui-components'
import { ERROR_STATUS_CODES } from 'constants/General'
import { useUpdateEarningsDistributionMutation } from 'hooks/mutations/Rewards/useUpdateEarningsDistributionMutation'
import { useMFAMutation } from 'hooks/mutations/useMFAMutation'
import { useCustomerEarningsSummaryQuery } from 'hooks/queries/Rewards/useCustomerEarningsSummaryQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { REWARDS_REFERENCE_PREFIX } from 'pages/Rewards/constants/const'
import { formatAmount } from 'pages/Rewards/utils'
import { handleOnMutationError } from 'utils/reactQuery/onMutationError'
import { generateReference } from 'utils/utils'
import { RewardsEmbeddedDisburseDialog } from './RewardsEmbeddedDisburseDialog'
import { RewardsEmbeddedEarningsChart } from './RewardsEmbeddedEarningsChart'
import { RewardsEmbeddedGiveBack } from './RewardsEmbeddedGiveBack'
import { RewardsEmbeddedSummary } from './RewardsEmbeddedSummary'

const USER_CURRENCY = 'USD'
interface RewardsEmbeddedCustomerProps {
  currency: string
}

export const RewardsEmbeddedCustomer = ({ currency }: RewardsEmbeddedCustomerProps) => {
  const { t } = useTranslation()
  const [openDisburseDialog, setOpenDisburseDialog] = useState(false)
  const [hasBeenDisbursed, setHasBeenDisbursed] = useState(false)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { data: customerEarningsSummary, isLoading: isLoadingCustomerEarningsSummary } =
    useCustomerEarningsSummaryQuery({
      currency,
    })

  const { mutate: updateEarningsDistribution, isPending: isUpdatingDistribution } =
    useUpdateEarningsDistributionMutation()

  const { mutate: initiateMFA } = useMFAMutation()

  const handleDisburse = (destinationWalletId: string, authenticationCode: string) => {
    resetRequestError()

    updateEarningsDistribution(
      {
        reference: generateReference(REWARDS_REFERENCE_PREFIX),
        destinationWalletId,
      },
      {
        onError: error => {
          if (error?.status === ERROR_STATUS_CODES.AUTH_REQUIRED) {
            initiateMFA(
              {
                challenge: error?.data?.challenge?.value,
                tokenResponse: authenticationCode,
                mfaCallbackUrl: error?.data?.callbackUrl,
              },
              {
                onSuccess: () => {
                  toast({
                    header: t('rewards.embeddedCustomers.disburseDialog.successMessage'),
                    variant: 'success',
                  })
                  setHasBeenDisbursed(true)
                  setOpenDisburseDialog(false)
                },
                onError: mfaError => {
                  setRequestError({
                    handledMessage:
                      mfaError?.status === ERROR_STATUS_CODES.FORBIDDEN
                        ? t('2faRejectedError')
                        : mfaError?.data?.message,
                    errorCode: mfaError?.status,
                    show: true,
                  })
                },
              }
            )
            return
          }
          handleOnMutationError(
            error,
            null,
            setRequestError,
            'updateEarningsDistribution',
            'updateEarningsDistribution'
          )
        },
      }
    )
  }

  const onOpenDisburseDialog = () => {
    setOpenDisburseDialog(true)
  }

  const myRewardsPayout = useMemo(() => {
    if (!customerEarningsSummary?.totalUnpaidEarnings) return `0.00 ${currency}`
    return `${formatAmount(Number.parseFloat(customerEarningsSummary.totalUnpaidEarnings))} ${currency}`
  }, [customerEarningsSummary?.totalUnpaidEarnings])

  return (
    <Box flexCol gap={16}>
      <Box
        gap={16}
        flexCol
        className={clsx({
          'xl:grid xl:grid-cols-2': !hasBeenDisbursed,
        })}
      >
        <RewardsEmbeddedSummary
          currency={currency}
          userCurrency={USER_CURRENCY}
          totalBalance={customerEarningsSummary?.totalBalance || '0'}
          totalEarnings={customerEarningsSummary?.totalEarnedAmount || '0'}
          hasBeenDisbursed={hasBeenDisbursed}
          isLoading={isLoadingCustomerEarningsSummary}
        />

        <RewardsEmbeddedGiveBack
          onDisburse={onOpenDisburseDialog}
          hasBeenDisbursed={hasBeenDisbursed}
        />
      </Box>

      <RewardsEmbeddedEarningsChart currency={currency} />

      <RewardsEmbeddedDisburseDialog
        onDisburse={handleDisburse}
        open={openDisburseDialog}
        onClose={() => {
          setOpenDisburseDialog(false)
          resetRequestError()
        }}
        myRewardsPayout={myRewardsPayout}
        currency={currency}
        isSubmitting={isUpdatingDistribution}
        requestError={requestError}
        onClearRequestError={resetRequestError}
      />
    </Box>
  )
}
